import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UI_COLORS, GRADIENTS, colorHelpers } from '../utils/colors';
import { usePortalAuth } from '../context/portalAuthContext';
import PortalLogin from './PortalLogin';
import PortalPurchasesPage from './pages/PortalPurchasesPage';
import PortalSalesPage from './pages/PortalSalesPage';
import PortalInquiriesPage from './pages/PortalInquiriesPage';
import PortalProfilePage from './pages/PortalProfilePage';
import PortalForceChangePassword from './pages/PortalForceChangePassword';
import PortalOnboardingProfile from './pages/PortalOnboardingProfile';
import PortalPendingApproval from './pages/PortalPendingApproval';

/**
 * Customer portal shell. No router — a simple `page` state swaps the three
 * content areas, mirroring the staff MainApp pattern. Renders the login screen
 * until authenticated. All three tabs show for both customers and suppliers
 * (§7 of the BE note); empty states are fine.
 */
const CustomerPortalApp = () => {
  const { t } = useTranslation();
  const nav = t('portal.nav', { returnObjects: true });
  const { isAuthenticated, isLoading, customer, profileFresh, logout } = usePortalAuth();
  const [page, setPage] = useState('purchases');

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2" style={{ borderColor: UI_COLORS.BRAND.TERTIARY }} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <PortalLogin />;
  }

  // Onboarding gate (order matters). Full portal requires approved AND the
  // temp password changed. Drive entirely off the live /portal/me `customer`.
  const status = (customer?.onboarding_status || 'approved').toLowerCase();
  const needsOnboarding = customer?.must_change_password === true
    || status === 'invited' || status === 'rejected' || status === 'profile_submitted';

  // On a hard refresh, `customer` is first restored from (possibly stale)
  // localStorage before the live /me lands. If the STORED state implies an
  // onboarding screen but we haven't confirmed it against a live /me yet, wait
  // rather than flash a screen the server may have moved past (e.g. pending →
  // approved). `approved` shows the portal immediately (the common case).
  if (needsOnboarding && !profileFresh) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2" style={{ borderColor: UI_COLORS.BRAND.TERTIARY }} />
      </div>
    );
  }

  if (customer?.must_change_password === true) {
    return <PortalForceChangePassword />;
  }
  if (status === 'invited' || status === 'rejected') {
    return <PortalOnboardingProfile />;
  }
  if (status === 'profile_submitted') {
    return <PortalPendingApproval />;
  }
  // status === 'approved' (or a pre-existing/manual customer with no status) →
  // the normal tabbed portal below.

  const tabs = [
    { id: 'purchases', label: nav?.purchases || 'Purchase Orders' },
    { id: 'sales', label: nav?.sales || 'Sales Orders' },
    { id: 'inquiries', label: nav?.inquiries || 'Inquiries' },
    { id: 'profile', label: nav?.profile || 'Profile' },
  ];

  const renderPage = () => {
    switch (page) {
      case 'sales': return <PortalSalesPage />;
      case 'inquiries': return <PortalInquiriesPage />;
      case 'profile': return <PortalProfilePage />;
      case 'purchases':
      default: return <PortalPurchasesPage />;
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.PRIMARY }}>
      {/* Top bar */}
      <header className="sticky top-0 z-20 shadow-sm" style={{ background: GRADIENTS.PRIMARY }}>
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-xs font-medium" style={{ color: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.text, 0.85) }}>
              {nav?.portalName || 'Customer Portal'}
            </div>
            <div className="text-lg font-bold truncate" style={{ color: UI_COLORS.BUTTONS.PRIMARY.text }}>
              {customer?.company_name || customer?.owner_name || '—'}
            </div>
          </div>
          <button
            type="button"
            onClick={logout}
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:opacity-90"
            style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.CARD, 0.2), color: UI_COLORS.BUTTONS.PRIMARY.text }}
          >
            {nav?.logout || 'Logout'}
          </button>
        </div>

        {/* Tab nav */}
        <nav className="max-w-6xl mx-auto px-4 flex gap-1">
          {tabs.map((tab) => {
            const active = page === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setPage(tab.id)}
                className="px-4 py-2.5 text-sm font-semibold transition-all border-b-2"
                style={{
                  color: UI_COLORS.BUTTONS.PRIMARY.text,
                  borderColor: active ? UI_COLORS.BUTTONS.PRIMARY.text : 'transparent',
                  opacity: active ? 1 : 0.75,
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {renderPage()}
      </main>
    </div>
  );
};

export default CustomerPortalApp;
